import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../components/layout';
import { Alert } from '../components/ui/alert';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Field } from '../components/ui/field';
import { Input } from '../components/ui/input';
import { api, errorMessage } from '../lib/api';
import { can, useMe } from '../lib/session';
import type { AdPerformance } from '../lib/types';
import { formatMoney } from '../lib/utils';

const iso = (d: Date) => d.toISOString().slice(0, 10);

/** Реклама: загрузка расходов из кабинетов (CSV) и эффективность каналов. */
export function AdsPage() {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const queryClient = useQueryClient();
  const [from, setFrom] = useState(iso(new Date(Date.now() - 30 * 86_400_000)));
  const [to, setTo] = useState(iso(new Date()));
  const [channel, setChannel] = useState('google_ads');
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();
  const report = useQuery({
    queryKey: ['ads', from, to],
    queryFn: () => api<AdPerformance>(`/ads/performance?from=${from}&to=${to}`),
  });
  const money = (minor: number | null) =>
    minor === null ? '—' : formatMoney(minor, 'EUR', i18n.language);

  const upload = async (file: File | undefined) => {
    if (!file) return;
    setError(undefined);
    setMessage(undefined);
    try {
      const result = await api<{ imported: number; errors: { line: number; message: string }[] }>(
        '/ads/spend/import',
        { method: 'POST', body: { csv: await file.text(), channel } },
      );
      setMessage(
        t('ads.imported', { count: result.imported }) +
          (result.errors.length
            ? ` ${t('ads.skipped', { lines: result.errors.map((e) => e.line).join(', ') })}`
            : ''),
      );
      await queryClient.invalidateQueries({ queryKey: ['ads'] });
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <>
      <PageHeader title={t('ads.title')} description={t('ads.hint')} />
      <div className="grid gap-6">
        {can(me, 'integrations.manage') ? (
          <Card>
            <CardHeader title={t('ads.import')} description={t('ads.importHint')} />
            <CardContent className="flex flex-wrap items-end gap-3 text-sm">
              <Field label={t('ads.channel')}>
                {(id) => (
                  <Input
                    id={id}
                    value={channel}
                    onChange={(e) => {
                      setChannel(e.target.value);
                    }}
                    className="w-40 font-mono"
                  />
                )}
              </Field>
              <Field label={t('ads.file')}>
                {(id) => (
                  <Input
                    id={id}
                    type="file"
                    accept=".csv,text/csv"
                    onChange={(e) => void upload(e.target.files?.[0])}
                  />
                )}
              </Field>
              {message ? <Alert variant="success">{message}</Alert> : null}
              {error ? <Alert variant="destructive">{error}</Alert> : null}
            </CardContent>
          </Card>
        ) : null}
        <Card>
          <CardHeader title={t('ads.performance')} description={t('ads.performanceHint')} />
          <CardContent className="grid gap-3 text-sm">
            <div className="flex gap-2">
              <Field label={t('ord.from')}>
                {(id) => (
                  <Input
                    id={id}
                    type="date"
                    value={from}
                    onChange={(e) => {
                      setFrom(e.target.value);
                    }}
                  />
                )}
              </Field>
              <Field label={t('ord.to')}>
                {(id) => (
                  <Input
                    id={id}
                    type="date"
                    value={to}
                    onChange={(e) => {
                      setTo(e.target.value);
                    }}
                  />
                )}
              </Field>
            </div>
            <table className="w-full">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1">{t('ads.channel')}</th>
                  <th className="text-right">{t('ads.spend')}</th>
                  <th className="text-right">{t('ads.orders')}</th>
                  <th className="text-right">{t('ads.revenue')}</th>
                  <th className="text-right">{t('ads.newCustomers')}</th>
                  <th className="text-right">CAC</th>
                  <th className="text-right">ROAS</th>
                </tr>
              </thead>
              <tbody>
                {(report.data?.rows ?? []).map((r) => (
                  <tr key={r.channel} className="border-t">
                    <td className="py-1 font-mono">{r.channel}</td>
                    <td className="text-right">{money(r.costMinor)}</td>
                    <td className="text-right">{r.orders}</td>
                    <td className="text-right">{money(r.revenueMinor)}</td>
                    <td className="text-right">{r.newCustomers}</td>
                    <td className="text-right">{money(r.cacMinor)}</td>
                    <td className="text-right">
                      {r.roas === null ? '—' : `${r.roas.toFixed(2)}×`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
